import { i18n } from '@/plugins/i18n';
import type { BaseTransaction } from '@/types/transactions';
import { camelCase, isEmpty, isNil, replace, startCase } from 'lodash-es';
import { useUserStore } from '@/stores/userStore';
import type { ProfileAccessService } from '@/types/users';
import type { MaybeArray } from '@/types/utilities';
import {
	type AgreementStatus,
	type ExchangeStatus,
	type LeaderboardStatus,
	type TransactionStatus,
	type SortDirection,
	type SortParam,
	type NilGoStatus,
} from '@teamworksdev/influencer/core';
import { rgbValuesToHex } from '@teamworksdev/vuetify';
import { hexToRgbValues } from './colorContrast';
import type { LocationQueryValue } from 'vue-router';

export function formatBytes(bytes: number, decimals = 1) {
	if (bytes == 0) return `${i18n.global.n(0)} ${i18n.global.t('fileSizeAbbreviations.bytes')}`;
	const k = 1024;
	const dm = decimals || 2;
	const sizes = [
		i18n.global.t('fileSizeAbbreviations.bytes'),
		i18n.global.t('fileSizeAbbreviations.kb'),
		i18n.global.t('fileSizeAbbreviations.mb'),
		i18n.global.t('fileSizeAbbreviations.gb'),
		i18n.global.t('fileSizeAbbreviations.tb'),
		i18n.global.t('fileSizeAbbreviations.pb'),
		i18n.global.t('fileSizeAbbreviations.eb'),
		i18n.global.t('fileSizeAbbreviations.zb'),
		i18n.global.t('fileSizeAbbreviations.yb'),
	];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return `${i18n.global.n(parseFloat((bytes / Math.pow(k, i)).toFixed(dm)))} ${sizes[i]}`;
}

export function formatUrlDisposition(dataurl: string, type: 'download' | 'view') {
	return new URL(dataurl).search ? dataurl + `&type=${type}` : dataurl + `?type=${type}`;
}

export function downloadUri(dataurl: string, filename: string) {
	const link = document.createElement('a');
	link.href = formatUrlDisposition(dataurl, 'download');
	link.download = filename;
	link.click();
	link.remove();
}

export function toggleItemInArray<T = string>(item: T, array: T[]) {
	if (array.includes(item)) {
		array.splice(array.indexOf(item), 1);
	} else {
		array.push(item);
	}
}

export const commaSeparate = (...values: Array<string | undefined | null>) => {
	return values.filter((i) => i).join(', ');
};

type FileSizeUnit = 'kb' | 'mb' | 'gb' | 'tb' | 'pb';
export const getBytes = (value: number, unit: FileSizeUnit): number => {
	const units: Record<FileSizeUnit, number> = {
		kb: 1,
		mb: 2,
		gb: 3,
		tb: 4,
		pb: 5,
	};

	const bytes = 1024 ** units[unit];
	return bytes * value;
};

export const formatSorts = <T extends string>(sorts: SortParam<T>[]) => {
	return sorts.map<[T, SortDirection]>(({ key, order }) => [key, order]);
};

export function isEmptyOrDefault(value: any, comparator: any) {
	return isEmpty(value) || value === comparator;
}

export function convertCentsToDollars(cents: number) {
	return cents / 100;
}

export const convertDollarsToCents = (amount: string | number) => {
	return typeof amount === 'string' ? (Number(amount.split('.')[0]) * 100) + Number(amount.split('.')[1]) : amount * 100;
};

export function statusInArray<T>(statuses: T[], status: T | undefined | null) {
	if (isNil(status)) return false;
	return statuses.includes(status);
}

export const exchangeStatusInArray = statusInArray<ExchangeStatus>;
export const leaderboardStatusInArray = statusInArray<LeaderboardStatus>;
export const agreementStatusInArray = statusInArray<AgreementStatus>;
export const transactionStatusInArray = statusInArray<TransactionStatus>;
export const nilGoStatusInArray = statusInArray<NilGoStatus>;

export const sortTransactions = (transactions: BaseTransaction[]) => {
	return transactions.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

export function capitalizeAndTrim(value: string | undefined) {
	return replace(startCase(camelCase(value)), /\s+/g, '');
}

export function resolveVisibilityByProfileAccess(access: MaybeArray<keyof ProfileAccessService> | undefined) {
	if (access === undefined) return true;
	const userStore = useUserStore();
	return access instanceof Array ? access.some((k) => userStore.profileAccess[k]) : !!userStore.profileAccess[access];
}

export function truncateString(str: string, count: number) {
	return str.length > count
		? `${str.slice(0, count)}...`
		: str;
}

export const normalizeToArray = <T>(value: MaybeArray<T>): T[] => {
	return Array.isArray(value) ? value : [value];
};

export function normalizeQueryValue(value: LocationQueryValue | LocationQueryValue[] | undefined): string | undefined {
	const first = Array.isArray(value) ? value[0] : value;
	return first ?? undefined;
}

export const evenlyDistributeAmounts = (totalAmount: number, divisor: number) => {
	if (divisor <= 0) {
		throw new Error('Divisor must be a positive integer');
	}

	const base = Math.floor(totalAmount / divisor);
	const remainder = totalAmount % divisor;
	const result = Array(divisor).fill(base);

	for (let i = 0; i < remainder; i++) {
		result[i] += 1;
	}

	return result;
};

/**
 * Joins an array of strings with a separator between each item,
 * using a different separator before the last item.
 *
 * @param items - The array of strings to join
 * @param separator - The separator to use between all items except the last (default: ', ')
 * @param lastSeparator - The separator to use before the last item (default: ' & ')
 * @returns A string with all items joined with the appropriate separators
 * @example
 * // Returns "apple, orange & banana"
 * joinWithLast(['apple', 'orange', 'banana']);
 *
 * // Returns "apple, orange or banana"
 * joinWithLast(['apple', 'orange', 'banana'], ', ', ' or ');
 */
export function joinWithLast(
	items: string[],
	separator: string = ', ',
	lastSeparator: string = ' & ',
): string {
	if (items.length < 2) {
		return items.join();
	}
	return items.slice(0, -1).join(separator) + lastSeparator + items.slice(-1);
}

/**
 * Removes all HTML tags from the given string and returns the plain text content.
 *
 * @param text - The input string containing HTML content.
 * @returns The plain text content with all HTML tags stripped out.
 */
export function stripHtml(text: string) {
	const doc = new DOMParser().parseFromString(text, 'text/html');
	return doc.body.textContent || '';
}

/**
 * Generates an array of hex color strings that represent a gradient between two colors.
 *
 * @param startHex - The starting hex color code (e.g., '#FF0000')
 * @param endHex - The ending hex color code (e.g., '#0000FF')
 * @param steps - The number of color steps to generate in the gradient (default: 10)
 * @returns An array of hex color strings representing the interpolated gradient
 *
 * @example
 * ```typescript
 * const gradient = interpolateHexColors('#FF0000', '#0000FF', 5);
 * // Returns: ['#FF0000', '#BF003F', '#7F007F', '#3F00BF', '#0000FF']
 * ```
 */

export function interpolateHexColors(startHex: string, endHex: string, steps = 10) {
	const start = hexToRgbValues(startHex);
	const end = hexToRgbValues(endHex);

	if (!start || !end) {
		console.warn('Invalid hex color values provided');
		console.warn({ startHex, endHex });
		return [];
	}

	const [r1, g1, b1] = start.split(',').map(Number);
	const [r2, g2, b2] = end.split(',').map(Number);

	if ([r1, g1, b1, r2, g2, b2].some((n) => isNaN(n as number))) {
		console.warn('Failed to parse RGB values');
		return [];
	}

	const colors = [];

	for (let i = 0; i < steps; i++) {
		const t = i / (steps - 1); // normalized interpolation factor [0,1]
		const r = Math.round(r1! + (r2! - r1!) * t);
		const g = Math.round(g1! + (g2! - g1!) * t);
		const b = Math.round(b1! + (b2! - b1!) * t);
		colors.push(rgbValuesToHex([r, g, b].join(',')));
	}

	return colors;
}
